<script setup>
import {
  trackClickEvent,
  formatDate,
  getCustomStationLabel,
} from "~/utilities/helpers"
import { useBreakpoints } from "~/composables/useBreakpoints"
import { stripShowUrl } from "~/composables/useNavigationData"
import useLiveStream from "~/composables/data/liveStream"
import {
  useAllCurrentStations,
  useIsApp,
  useCurrentEpisodeHolder,
  useIsDarkMode,
} from "~/composables/states"
import { useDebounceFn } from "@vueuse/core"
import { getSchedulePdfLink } from "~/server/utils/liveSchedule"
import {
  scheduleLocalNotification,
  getEntryTitle,
} from "~/utilities/local-notifications"

const {
  getTheTime,
  allLiveScheduleData,
  fetchScheduleSimple,
  currentScheduleDate,
  nextDayScheduleDate,
  previousDayScheduleDate,
  setToNextDay,
  setToPreviousDay,
  isToday,
  createScheduleAbortController,
} = useLiveStream()

const allCurrentStations = useAllCurrentStations()
const isApp = useIsApp()
const isDarkMode = useIsDarkMode()
const currentEpisodeHolder = useCurrentEpisodeHolder()
const { isMobileBreakpoint } = useBreakpoints()

const disabledScheduleDate = ref(null)
onMounted(() => {
  const futureDisabledDate = new Date()
  futureDisabledDate.setDate(futureDisabledDate.getDate() + 6)
  disabledScheduleDate.value = futureDisabledDate
})

// schedule a local notification and track it
const handleScheduleLocalNotification = async (entry) => {
  trackClickEvent(
    "Click Tracking - Schedule Notify Button",
    "Live Page",
    `Notify me about ${entry.station} - ${getEntryTitle(entry)} at ${
      entry.attributes.start
    }`
  )
  entry.station = currentEpisodeHolder.value.station
  await scheduleLocalNotification(entry)
}

watch(
  allCurrentStations,
  async () => {
    allLiveScheduleData.value = await Promise.all(
      allCurrentStations.value.map((station) => {
        return fetchScheduleSimple(station, new Date())
      })
    )
  },
  { once: true }
)

// Fetch all schedule data for all stations
const getAllScheduleData = async () => {
  // Don't fetch if stations aren't loaded yet
  if (!allCurrentStations.value || allCurrentStations.value.length === 0) {
    return
  }

  // Filter out the holiday channel for now since there is no schedule data
  allCurrentStations.value = allCurrentStations.value.filter(
    (station) => station.slug !== "wqxr-holiday-channel-on-wnyc"
  )

  allLiveScheduleData.value = []

  // Create new abort controller (automatically aborts any existing fetches)
  const abortController = createScheduleAbortController()

  try {
    const results = await Promise.all(
      allCurrentStations.value.map((station) => {
        return fetchScheduleSimple(
          station,
          currentScheduleDate.value,
          abortController.signal
        )
      })
    )

    // Filter out null results (aborted requests)
    allLiveScheduleData.value = results.filter((result) => result !== null)
  } catch (error) {
    console.error("Error in getAllScheduleData:", error)
  }
}

// Debounce the schedule data fetching to prevent rapid successive calls
const debouncedGetAllScheduleData = useDebounceFn(getAllScheduleData, 300)

watch(currentScheduleDate, () => {
  allLiveScheduleData.value.length = 0
  debouncedGetAllScheduleData()
})

onMounted(() => {
  // Wait for allCurrentStations to be populated before fetching schedule data
  if (allCurrentStations.value && allCurrentStations.value.length > 0) {
    getAllScheduleData()
  } else {
    const stopWatcher = watchEffect(() => {
      if (allCurrentStations.value && allCurrentStations.value.length > 0) {
        getAllScheduleData()
        stopWatcher() // Stop watching once data is available
      }
    })
  }
})

// handle the PDF download button
const handleScheduleDownload = () => {
  trackClickEvent(
    "Click Tracking - Schedule Download Button",
    "Live Page",
    "download schedule PDF"
  )
  // need to pull this from the CMS
  window.open(getSchedulePdfLink(), "_blank")
}

// handles the click on the show title
const toShowPageClick = (entry, current = false) => {
  const title = entry.attributes.parentTitle
  const slug = stripShowUrl(entry.attributes.parentUrl)
  trackClickEvent(
    `Click Tracking - ${
      current ? "current live show " : "Show Title"
    } - ${title}`,
    "Schedule",
    title
  )
  navigateTo(`/browse/shows/${slug}`)
}

// determine if the entry is the current episode and if the first entry should be featured (New Sounds (q2) and the holiday channel are excluded)
const handleCurrentEpisode = (entry, index) => {
  const badSlugs = ["q2", "wqxr-holiday-channel-on-wnyc"]
  return isToday.value && index === 0 && !badSlugs.includes(entry.slug)
}

// handle the next and prev buttons for the schedule. Hide the label at a smaller size
const handleScheduleNavigationButtonLabel = (date) => {
  if (!isMobileBreakpoint.value) {
    return formatDate(date, "EEEE")
  } else {
    return null
  }
}
//isDarkMode.value = true
</script>

<template>
  <div class="schedule" :class="{ 'dark-mode': isDarkMode }">
    <div class="flex flex-wrap justify-content-between align-items-center mb-4">
      <h2 class="text-xl md:text-5xl">Schedule</h2>
      <Button
        v-if="!isApp"
        severity="secondary"
        variant="link"
        class="link -mr-2 text-sm md:text-base"
        @click="handleScheduleDownload"
        label="Weekly Schedule (pdf)"
      ></Button>
    </div>
    <Tabs
      class="schedule-station-tabs relative"
      value="0"
      scrollable
      v-if="allCurrentStations"
    >
      <TabList>
        <Tab
          v-for="(entry, index) in allCurrentStations"
          :key="index"
          :value="index.toString()"
          >{{ getCustomStationLabel(entry.station) }}</Tab
        >
        <!-- blank entry for spacing -->
        <div>&nbsp;</div>
      </TabList>
      <hr class="w-full mt-5 opacity-40" />
      <div
        class="date-tools flex justify-content-between align-items-center my-4"
      >
        <Button
          severity="secondary"
          variant="text"
          class="-ml-3"
          @click="setToPreviousDay()"
          :label="handleScheduleNavigationButtonLabel(previousDayScheduleDate)"
          icon="pi pi-chevron-left"
          :class="
            formatDate(currentScheduleDate, 'LLLL d, yyyy') ===
            formatDate(Date(), 'LLLL d, yyyy')
              ? 'pointer-events-none opacity-50'
              : 'day-change-btn link'
          "
        ></Button>
        <div
          class="today flex flex-column gap-0 align-items-center text-center"
        >
          <span class="day font-bold text-lg">{{
            formatDate(currentScheduleDate, "EEEE")
          }}</span>
          <span class="date text-sm">{{
            formatDate(currentScheduleDate, "LLLL d, yyyy")
          }}</span>
        </div>
        <Button
          severity="secondary"
          variant="text"
          iconPos="right"
          class="day-change-btn link -mr-3"
          @click="setToNextDay()"
          :label="handleScheduleNavigationButtonLabel(nextDayScheduleDate)"
          icon="pi pi-chevron-right"
          :class="
            disabledScheduleDate &&
            formatDate(currentScheduleDate, 'LLLL d, yyyy') ===
              formatDate(disabledScheduleDate, 'LLLL d, yyyy')
              ? 'pointer-events-none opacity-50'
              : 'day-change-btn link'
          "
        ></Button>
      </div>

      <TabPanels>
        <TabPanel
          v-for="(data, index) in allLiveScheduleData"
          :key="index"
          :value="index.toString()"
          :class="[{ selected: index === 0 }]"
        >
          <div class="flex flex-column gap-1">
            <div
              v-for="(entry, entryIndex) in data"
              :key="entryIndex"
              class="schedule-entry flex justify-content-between align-items-stretch gap-3 style-mode-light light-mode p-ripple cursor-pointer"
              v-ripple
              :class="
                handleCurrentEpisode(entry, entryIndex)
                  ? 'selected py-3 md:p-0'
                  : ''
              "
              @click="
                handleCurrentEpisode(entry, entryIndex)
                  ? toShowPageClick(entry, true)
                  : toShowPageClick(entry)
              "
            >
              <div
                class="active-content flex flex-column justify-content-between w-full"
              >
                <div>
                  <p class="time">
                    {{
                      getTheTime(
                        entry.attributes.start,
                        entry.attributes.end,
                        entryIndex
                      )
                    }}
                  </p>
                  <h2 class="title truncate t2lines">
                    {{ getEntryTitle(entry) }}
                  </h2>
                  <HtmlConvert
                    v-if="
                      entry.station.episodeBody &&
                      handleCurrentEpisode(entry, entryIndex)
                    "
                    :htmlContent="entry.station.episodeBody"
                    class="desc truncate t3lines mt-1"
                    no-blocks
                  />
                </div>
              </div>
              <div
                v-if="
                  handleCurrentEpisode(entry, entryIndex) &&
                  entry.station.onTodaysShowImageTemplate
                "
                class="hidden md:block"
              >
                <!-- <pre>{{ entry.station }}</pre> -->
                <VImage
                  :src="
                    entry.station.onTodaysShowImageTemplate ||
                    entry.station.onTodaysShowImage
                  "
                  :alt="
                    entry.station.onTodaysShowImageAltText ||
                    'on today\'s show image'
                  "
                  :size="{ xs: [208, 208] }"
                  :allowVerticalEffect="true"
                  class="flex-none w-13rem"
                  :srcset="[2]"
                />
              </div>
              <Button
                v-if="isApp && entryIndex > 0"
                severity="secondary"
                text
                plain
                rounded
                class="flex-none z-1"
                aria-label="set notification"
                @click.stop="handleScheduleLocalNotification(entry)"
              >
                <template #icon>
                  <NotificationIcon :entry="entry" />
                </template>
              </Button>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
    <div v-else class="skeleton mt-5">
      <div class="flex gap-6 mb-5">
        <Skeleton
          v-for="i in 4"
          :key="`tabs-skeleton-${i}`"
          height="20px"
          width="150px"
          borderRadius="4px"
        />
      </div>
      <hr />
      <div class="flex justify-content-between my-6">
        <Skeleton height="20px" width="110px" borderRadius="4px" />
        <div class="flex flex-column gap-2">
          <Skeleton height="16px" width="110px" borderRadius="4px" />
          <Skeleton height="10px" width="110px" borderRadius="4px" />
        </div>
        <Skeleton height="20px" width="110px" borderRadius="4px" />
      </div>
    </div>
    <div
      v-if="!allLiveScheduleData.length > 0"
      class="skeleton flex flex-column gap-5 -mt-3"
    >
      <Skeleton
        v-if="isToday"
        height="213px"
        width="100%"
        borderRadius="10px"
        class="-ml-3 -mr-3 xl:mr-0 hidden md:block"
      />
      <div
        v-for="i in 9"
        :key="`schedule-skeleton-${i}`"
        class="flex align-items-center justify-content-between pr-2"
      >
        <div class="flex gap-3">
          <div class="flex flex-column gap-2">
            <Skeleton
              class="opacity-50"
              height="14px"
              width="64px"
              borderRadius="4px"
            />
            <Skeleton height="22px" width="174px" borderRadius="4px" />
          </div>
        </div>
        <Skeleton
          :class="[{ 'opacity-0': i < 1 }]"
          height="26px"
          width="26px"
          borderRadius="15px"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
html {
  // &.style-mode-dark {
  //   .schedule {
  //     .schedule-entry {
  //       &.selected {
  //         background-color: #ffffff1a;
  //         padding: 0.75rem 0.5rem 0.75rem 0;
  //         border-radius: 8px;
  //         .left {
  //           border: none;
  //         }
  //       }
  //     }
  //   }
  // }
  .schedule {
    margin-bottom: 50px;
    // hide arrows
    .p-tabs {
      .p-tablist {
        margin-left: -3rem;
        margin-right: -3rem;
        -webkit-mask-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 0) 0%,
          rgb(0, 0, 0) 6%,
          rgb(0, 0, 0) 94%,
          rgba(0, 0, 0, 0) 100%
        );
        mask-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 0) 0%,
          rgb(0, 0, 0) 6%,
          rgb(0, 0, 0) 94%,
          rgba(0, 0, 0, 0) 100%
        );
        @include media("<md") {
          margin-left: -1.5rem;
          margin-right: -1.5rem;
        }

        .p-tablist-content {
          padding-left: 3rem;
          @include media("<md") {
            padding-left: 1.5rem;
          }
          .p-tab {
            &:last-child {
              padding-right: 1.5rem;
            }
          }
        }
        .p-tablist-nav-button {
          box-shadow: none;
          background-color: transparent;
          svg,
          span {
            display: none;
          }
        }
        // .p-tablist-prev-button {
        // }
        // .p-tablist-next-button {
        // }
      }
    }

    .schedule-entry {
      .date-tools {
        .day-change-btn {
          .p-button-label {
            @include media("<sm") {
              display: none;
            }
          }
        }
      }
      /*    &.selected {
        *:not(.p-button .p-button-label) {
          @include media(">md") {
            color: var(--p-surface-950) !important;
          }
        }
      } */
    }
  }
}
</style>
<style lang="scss" scoped>
.schedule {
  .schedule-station-tabs {
    // &:before {
    //   background-color: var(--p-tabs-nav-button-background);
    //   //background-color: var(--p-darkblue-500);
    //   width: 100vw;
    //   height: 4.6rem;
    //   content: "";
    //   position: absolute;
    //   top: -1rem;
    //   left: -3.5em;
    //   @include media("<md") {
    //     left: -2em;
    //   }
    // }
  }
  @mixin selectedEntry {
    background-color: var(--p-content-background);
    border-radius: var(--p-border-radius-xl);
    overflow: hidden;
    .active-content {
      padding: 1rem 1rem 0.5rem 1rem;
    }
    &:before {
      display: none;
    }
  }
  .schedule-entry {
    position: relative;
    padding: 1rem;
    border-radius: var(--p-border-radius-xl);
    &:before {
      content: "";
      border: 2px solid transparent;
      border-color: var(--p-primary-500);
      border-radius: 8px;
      margin-right: 1rem;
      position: absolute;
      height: 80%;
      top: 10%;
      bottom: 0;
      display: none;
    }

    .active-content {
      min-height: 0; // Allow flex shrinking
    }
    .more-from {
      margin-left: -0.8rem;
      display: none;
    }
    &.selected {
      padding: 0;
      &:before {
        display: block;
      }
      .more-from {
        display: block;
      }
      .active-content {
        padding: 0rem 1rem 0rem 1rem;
      }
      @include media(">=md") {
        @include selectedEntry();
      }
      @include media("<md") {
        .more-from,
        .desc {
          display: none;
        }
      }
    }
    .follow-icon {
      width: 28px;
      height: 28px;
    }
    .title {
      transition: color var(--p-transition-duration);
      -webkit-transition: color var(--p-transition-duration);
    }
    &:hover {
      .title {
        color: var(--link-button-hover-color);
        text-decoration: underline;
      }
    }
  }
  &.dark-mode {
    .schedule-entry.selected {
      @include selectedEntry();
      .active-content {
        padding-top: 0.5rem;
      }
    }
  }
}
</style>
